import { BoundingBox, Emotion, FaceData, FaceQualityScores, PairedPersonType, PairingResponse, PersonPaginate, PersonType, Pose, Quality } from "../types/PersonType";
import { useAPI } from "../hooks/useAPI";
import { OptionType, ResponseErrorType } from "../types/CommonType";
import { LANG_COMMON_DONATED_MATERIAL_OVULE, LANG_COMMON_DONATED_MATERIAL_SEMEN } from "../lang";

export function PersonModel() {
    const { api } = useAPI();

    const create = async (person: PersonType) => {
        return new Promise<ResponseErrorType | PersonType>((resolve) => {
            api()
                .post("/api/person", person)
                .then((resp) => {
                    resolve(resp.data.person);
                })
                .catch((err) => {
                    resolve({ code: err.response.status, message: err.response.data.message, error: true })
                });
        });
    }

    const update = async (person_id: number, person: PersonType) => {
        return new Promise<boolean | PersonType>((resolve) => {
            api()
                .put(`/api/person/${person_id}`, person)
                .then((resp) => {
                    resolve(resp.data.person);
                })
                .catch(() => resolve(false));
        });
    }

    const list = async (type: string, business_id: number | undefined = undefined, options: any | undefined = undefined, pageNumber: number = 1, perPage: number = 10) => {
        return new Promise<boolean | PersonPaginate>((resolve) => {
            api()
                .get(`/api/person?business_id=${(business_id !== undefined) ? business_id : ""}&count=${perPage}&type=${type}&page=${pageNumber}&${new URLSearchParams(options).toString()}`)
                .then((resp) => {
                    resolve(resp.data);
                })
                .catch(() => resolve(false));
        });
    }

    const get = async (person_id: number | string) => {
        return new Promise<PersonType | boolean>((resolve) => {
            api()
                .get(`/api/person/${person_id}`)
                .then((resp) => {
                    resolve(resp.data);
                })
                .catch(() => resolve(false));
        });
    }

    const getPairingUrl = (person_id: number | string, options: any) => {
        let filteredOptions: any = {};
        for (let i in options) {
            console.log(i, options[i])
            if (options[i] !== null) filteredOptions[i] = options[i];
        }
        return `/api/person/${person_id}/pairing?${new URLSearchParams({ ...filteredOptions, cache: (new Date()).getTime() }).toString()}`;
    }

    const pairing = async (person_id: number | string, options: any) => {
        return new Promise<PairingResponse | boolean>((resolve) => {
            api()
                .get(getPairingUrl(person_id, options))
                .then((resp) => {
                    resolve(resp.data);
                })
                .catch(() => resolve(false));
        });
    }

    const remove = async (person_id: number) => {
        return new Promise<boolean>((resolve) => {
            api()
                .del(`/api/person/${person_id}`)
                .then((resp) => {
                    resolve(true);
                })
                .catch(() => resolve(false));
        });
    }

    const getBloodTypes = (): Array<OptionType> => {
        return ["A", "B", "O", "AB"].map((item) => {
            return { label: item, value: item };
        });
    }

    const getRaceTypes = (): Array<OptionType> => {
        return [
            { label: "Branco", value: "B" },
            { label: "Negro", value: "N" },
            { label: "Pardo", value: "P" },
            { label: "Asiático", value: "A" },
            { label: "Indígena", value: "I" },
        ];
    }

    const getDonatedMaterialTypes = (): Array<OptionType> => {
        return [
            { label: LANG_COMMON_DONATED_MATERIAL_OVULE.defaultMessage, value: "ovule"},
            { label: LANG_COMMON_DONATED_MATERIAL_SEMEN.defaultMessage, value: "semen"}
        ];
    }

    const getSkinColors = (): Array<OptionType> => {
        return [
            { label: "Tipo I (Branca-pálida)", value: "1" },
            { label: "Tipo II (Branca)", value: "2" },
            { label: "Tipo III (Branca - Bronzeia)", value: "3" },
            { label: "Tipo IV (Castanha-clara)", value: "4" },
            { label: "Tipo V (Parda)", value: "5" },
            { label: "Tipo VI (Negra)", value: "6" },
        ];
    }

    const getEyesColors = (): Array<OptionType> => {
        return [
            { label: "Azul", value: "A" },
            { label: "Azul-esverdeado", value: "AE" },
            { label: "Cinza", value: "CI" },
            { label: "Verde", value: "V" },
            { label: "Castanho esverdeado", value: "CEV" },
            { label: "Mel", value: "MEL" },
            { label: "Castanho claro", value: "CC" },
            { label: "Castanho escuro", value: "CE" },
        ];
    }

    const getHairColors = (): Array<OptionType> => {
        return [
            { label: "Loiro", value: "LOI" },
            { label: "Ruivo", value: "RUI" },
            { label: "Castanho claro", value: "CAC" },
            { label: "Castanho escuro", value: "CAE" },
            { label: "Preto", value: "PRE" },
        ];
    }

    const getHairTypes = (): Array<OptionType> => {
        return [
            { label: "Liso", value: "LIS" },
            { label: "Ondulado", value: "OND" },
            { label: "Cacheado", value: "CAC" },
            { label: "Crespo", value: "CRE" },
        ];
    }

    const getHeightTypes = (): Array<OptionType> => {
        return [
            { label: "<= 1,49m", value: "149" },
            { label: "1,50m - 1,54m", value: "154" },
            { label: "1,55m - 1,59m", value: "159" },
            { label: "1,60m - 1,64m", value: "164" },
            { label: "1,65m - 1,69m", value: "169" },
            { label: "1,70m - 1,74m", value: "174" },
            { label: "1,75m - 1,79m", value: "179" },
            { label: ">= 1,80m", value: "180" },
        ];
    }

    const getBMITypes = (): Array<OptionType> => {
        return [
            { label: "<18,5", value: "185" },
            { label: "18,5 - 24,9", value: "249" },
            { label: "25 - 29,9", value: "299" },
            { label: "30 - 34,9", value: "349" },
            { label: "35 - 39,9", value: "399" },
            { label: ">40", value: "400" },
        ];
    }

    const getSchooling = (): Array<OptionType> => {
        return [
            { label: "Superior Completo", value: "1" },
            { label: "Superior Incompleto", value: "2" },
            { label: "Ensino Médio Completo", value: "3" },
            { label: "Ensino Médio Incompleto", value: "4" },
            { label: "Ensino Fundamental Completo", value: "5" },
            { label: "Ensino Fundamental Incompleto", value: "6" },
        ];
    }

    const getOption = (needle: string | undefined | null, options: Array<OptionType> | undefined): OptionType | undefined => {
        if (needle === undefined || needle === null || options === undefined) return undefined;

        for (let i in options) {
            if (options[i].value === needle.toString())
                return options[i];
        }

        return undefined;
    }

    function getFaceAreaScore(boundingBox: BoundingBox): number {
        const area = boundingBox.Width * boundingBox.Height;
        const score = Math.min((area / 0.10) * 100, 100);
        return parseFloat(score.toFixed(2));
    }

    function getImageQualityScore(quality: Quality): number {
        const brightness = quality.Brightness;
        const sharpness = quality.Sharpness;

        const brightnessScore = 100 - Math.abs(brightness - 85) * 2;
        const avg = (Math.max(0, brightnessScore) + sharpness) / 2;

        return parseFloat(avg.toFixed(2));
    }


    function getPoseScore(pose: Pose): number {
        const penalty = (v: number) => Math.max(0, Math.abs(v) - 5) * 2;

        const totalPenalty = penalty(pose.Roll) + penalty(pose.Yaw) + penalty(pose.Pitch);
        const score = Math.max(0, 100 - totalPenalty);

        return parseFloat(score.toFixed(2));
    }

    function getObstructionScore(face: FaceData): number {
        let penalty = 0;

        if (face.Beard?.Value) penalty += 5;
        if (face.EyesOpen && !face.EyesOpen.Value) penalty += 20;
        if (face.MouthOpen?.Value) penalty += 10;
        if (face.Sunglasses?.Value) penalty += 30;

        const score = Math.max(0, 100 - penalty);
        return parseFloat(score.toFixed(2));
    }

    function getExpressionScore(emotions: Emotion[]): number {
        if (!Array.isArray(emotions) || emotions.length === 0) return 0;

        const mainEmotion = emotions.reduce((prev, curr) =>
            curr.Confidence > prev.Confidence ? curr : prev
        );

        const neutralEmotions = ["CALM", "NEUTRAL"];
        return neutralEmotions.includes(mainEmotion.Type.toUpperCase()) ? 100 : 60;
    }

    function getFaceQualityScores(face: FaceData): FaceQualityScores {
        try {
            return {
                faceArea: getFaceAreaScore(face.BoundingBox),
                quality: getImageQualityScore(face.Quality),
                pose: getPoseScore(face.Pose),
                obstructions: getObstructionScore(face),
                expression: getExpressionScore(face.Emotions),
            };
        } catch (error) {
            console.error("Error calculating face quality scores:", error);
            return {
                faceArea: 0,
                quality: 0,
                pose: 0,
                obstructions: 0,
                expression: 0,
            };
        }
    }

    function getFaceQualityFinalScore(scores: FaceQualityScores): number {
        try {
            const weights = {
                quality: 0.1,
                pose: 0.6,
                expression: 0.3,
            };

            const weightedSum = 
                (scores.quality * weights.quality) +
                (scores.pose * weights.pose) +
                (scores.expression * weights.expression);

            return parseFloat(weightedSum.toFixed(2));
        } catch (error) {
            console.error("Error calculating face quality final score:", error);
            return 0;
        }
    }
    
    const toPairedPerson = (person: PersonType): PairedPersonType => {
        return {
            id: person.id ?? 0,
            name: person.name ?? "",
            medical_record_number: person.medical_record_number ?? "",
            business_id: person.business_id ?? 0,
            blood_type: person.blood_type ?? "",
            race: person.race ?? "",
            skin_color: person.skin_color ?? "",
            eyes_color: person.eyes_color ?? "",
            hair_color: person.hair_color ?? "",
            hair_type: person.hair_type ?? "",
            height: person.height ?? "",
            height_number: person.height_number ?? "",
            weight: person.weight ?? "",
            similarity: "--",
            soft_similarity: "--",
            soft_face_similarity: "--",
            soft_physical_similarity: "--",
            donor: person.id ?? 0,
            receiver: person.id ?? 0,
            face_similarity: "--",
            skin_color_similarity: "--",
            eyes_color_similarity: "--",
            hair_type_similarity: "--",
            hair_color_similarity: "--",
            height_similarity: "--",
            bmi_similarity: "--",
            bmi: person.bmi ?? "",
            birth_date: person.birth_date ?? "",
            last_freezed_rod_date: null,
            face_details: person.face_details ?? null
        };
    };

    return {
        create,
        update,
        remove,
        list,
        get,
        getPairingUrl,
        pairing,
        getBloodTypes,
        getRaceTypes,
        getSkinColors,
        getEyesColors,
        getHairColors,
        getHairTypes,
        getHeightTypes,
        getBMITypes,
        getSchooling,
        getOption,
        getFaceQualityScores,
        getFaceQualityFinalScore,
        toPairedPerson,
        getDonatedMaterialTypes
    };
}